import React, { useRef, useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import { Edit2, X } from 'lucide-react';
import { ENDPOINTS } from '../../helpers/constants';
import RichTextEditor from '../common/RichTextEditor';
import ContactPicker from './ContactPicker';
import HoaDirectoryAutocomplete from './HoaDirectoryAutocomplete';
import InstructionFilesUpload from './InstructionFilesUpload';
import OrderingPlatformField from './OrderingPlatformField';
import { getDirectoryNameError, resolveEntityId, toTitleCase } from '../HoaDirectory/HoaDirectoryHelper';
import FormattedPhoneField from './FormattedPhoneField';

const ManagementCompanyFormFields = ({
  index = 0,
  hoa,
  onChange,
  fieldInvalid = {},
  managementCompanyRequired = false,
  directoryPickerOnly = false,
  contactsOnly = false,
  managementCompanyCreateMode = false,
  /** Add Management Company modal — contacts are created inline, not searched. */
  directoryCreateMode = false,
  directoryEditMode = false,
  managementCompanyContacts = [],
  managementCompanyFiles = [],
  pendingManagementFiles = [],
  removedManagementFileIds = [],
  onSelectCompany,
  onCreateCompany,
  onEditCompany,
  onLinkContact,
  onUnlinkContact,
  onUpdateContact,
  onPreferredContactChange,
  showPreferredContactToggle = false,
  onPendingFilesChange,
  onRemovedFileIdsChange,
  preferredAssociationId: preferredAssociationIdProp,
}) => {
  const preferredAssociationId =
    resolveEntityId(preferredAssociationIdProp) ?? resolveEntityId(hoa.associationId);
  const [editingMgmtCompanyName, setEditingMgmtCompanyName] = useState(false);
  const mgmtCompanyNameBeforeEditRef = useRef('');

  const updateFields = fields => onChange(fields);

  const showCompanyInvalid = fieldInvalid.company && managementCompanyRequired;
  const companyNameError =
    getDirectoryNameError(hoa.managementCompany, 'Management company') ||
    (showCompanyInvalid ? 'Company name is required when managed' : '');
  const showCompanyNameInvalid = Boolean(companyNameError);
  const hasCompanyId = Boolean(resolveEntityId(hoa.managementCompanyId));
  const hasInlineDraftCompany =
    !managementCompanyCreateMode &&
    !contactsOnly &&
    !hasCompanyId &&
    Boolean(String(hoa.managementCompany || '').trim());
  const showCompanyDetails = contactsOnly
    ? hasCompanyId
    : managementCompanyCreateMode || hasCompanyId || hasInlineDraftCompany;

  const showDirectoryEditPencil = contactsOnly && hasCompanyId && onEditCompany;
  const showCompanyNameLabel = !directoryPickerOnly;
  const companyNameLabel = directoryEditMode ? 'Company Name' : 'Company';

  const companyNameField = managementCompanyCreateMode ? (
    <Form.Control
      type="text"
      size="sm"
      value={hoa.managementCompany}
      onChange={e => updateFields({ managementCompany: e.target.value })}
      onBlur={e => updateFields({ managementCompany: toTitleCase(e.target.value) })}
      placeholder="Enter company name"
      isInvalid={showCompanyNameInvalid}
    />
  ) : !directoryPickerOnly && editingMgmtCompanyName && hasCompanyId ? (
    <Form.Control
      type="text"
      size="sm"
      value={hoa.managementCompany}
      onChange={e => updateFields({ managementCompany: e.target.value })}
      onBlur={e => updateFields({ managementCompany: toTitleCase(e.target.value) })}
      placeholder="Enter company name"
      isInvalid={showCompanyNameInvalid}
      autoFocus
    />
  ) : (
    <HoaDirectoryAutocomplete
      value={hoa.managementCompany}
      selectedId={hoa.managementCompanyId}
      invalid={showCompanyNameInvalid}
      placeholder="Type to search directory…"
      searchEndpoint={ENDPOINTS.MANAGEMENT_COMPANIES_SEARCH}
      creatableLabel="Add company"
      mapOption={c => ({
        id: c._id,
        label: c.name,
        subtitle: `${c.contactCount || (c.contacts || []).length || 0} contacts · ${
          c.phone || ''
        }`.trim(),
      })}
      onSelect={onSelectCompany}
      onCreateNew={onCreateCompany}
    />
  );

  return (
    <div className="border rounded p-3" style={{ background: '#e6f1fb', borderColor: '#185fa5' }}>
      <div
        className="text-uppercase font-weight-bold xsmallFont mb-2 d-flex align-items-center w-100"
        style={{ color: '#185fa5' }}
      >
        Management company
        {!directoryEditMode && managementCompanyRequired && (
          <span className="text-danger ml-1">*</span>
        )}
        {showDirectoryEditPencil && (
          <Button
            type="button"
            variant="link"
            className="mr-1 p-0 ml-auto d-inline-flex align-items-center text-secondary border-0 shadow-none"
            style={{ minWidth: 'auto', gap: 3, lineHeight: 1 }}
            onClick={onEditCompany}
          >
            <Edit2 size={10} color="var(--secondary)" className="flex-shrink-0" />
            <span className="tinyFont">Edit</span>
          </Button>
        )}
      </div>

      <div className="d-flex flex-wrap align-items-end mb-2" style={{ gap: 8 }}>
        <div
          style={{
            flex: showCompanyDetails && !contactsOnly ? '2 1 180px' : '1 1 100%',
            minWidth: 0,
          }}
        >
          <Form.Group className="mb-0">
            {(showCompanyNameLabel ||
              (!managementCompanyCreateMode && !directoryPickerOnly && hasCompanyId)) && (
              <div
                className="d-flex align-items-center w-100"
                style={{ minHeight: 18 }}
              >
                {showCompanyNameLabel && (
                  <Form.Label className="small mb-0 d-flex align-items-center">
                    <span>
                      {companyNameLabel}
                      {directoryEditMode && managementCompanyRequired && (
                        <span className="text-danger">*</span>
                      )}
                    </span>
                  </Form.Label>
                )}
                {!managementCompanyCreateMode && !directoryPickerOnly && hasCompanyId && (
                  <Button
                    type="button"
                    variant="link"
                    className={`mr-1 p-0 ml-auto d-inline-flex align-items-center border-0 shadow-none ${
                      editingMgmtCompanyName ? 'text-danger' : 'text-secondary'
                    }`}
                    style={{ minWidth: 'auto', gap: 3, lineHeight: 1 }}
                    onMouseDown={e => {
                      if (editingMgmtCompanyName) e.preventDefault();
                    }}
                    onClick={() => {
                      if (editingMgmtCompanyName) {
                        updateFields({ managementCompany: mgmtCompanyNameBeforeEditRef.current });
                        setEditingMgmtCompanyName(false);
                        return;
                      }
                      mgmtCompanyNameBeforeEditRef.current = hoa.managementCompany || '';
                      setEditingMgmtCompanyName(true);
                    }}
                  >
                    {editingMgmtCompanyName ? (
                      <>
                        <X size={12} color="var(--danger)" className="flex-shrink-0" />
                        <span className="tinyFont">Cancel</span>
                      </>
                    ) : (
                      <>
                        <Edit2 size={10} color="var(--secondary)" className="flex-shrink-0" />
                        <span className="tinyFont">Edit</span>
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}
            <div style={{ marginBottom: 0, lineHeight: 1 }}>{companyNameField}</div>
          </Form.Group>
        </div>

        {showCompanyDetails && !contactsOnly && (
          <>
            <div style={{ flex: '1 1 120px', minWidth: 0 }}>
              <Form.Group className="mb-0">
                <Form.Label className="small mb-0 d-block" style={{ minHeight: 18, marginBottom: 4 }}>
                  Phone
                </Form.Label>
                <FormattedPhoneField
                  value={hoa.companyPhone || ''}
                  onChange={e => updateFields({ companyPhone: e.target.value })}
                />
              </Form.Group>
            </div>
            <div style={{ flex: '1 1 140px', minWidth: 0 }}>
              <Form.Group className="mb-0">
                <Form.Label className="small mb-0 d-block" style={{ minHeight: 18, marginBottom: 4 }}>
                  Email
                </Form.Label>
                <Form.Control
                  type="email"
                  size="sm"
                  value={hoa.companyEmail || ''}
                  isInvalid={Boolean(fieldInvalid.companyEmail)}
                  onChange={e => updateFields({ companyEmail: e.target.value })}
                />
                {fieldInvalid.companyEmail && (
                  <p className="xsmallFont text-rush mb-0 mt-1">Enter a valid email</p>
                )}
              </Form.Group>
            </div>
          </>
        )}
      </div>

      {companyNameError && (
        <p className="xsmallFont text-rush mb-2">{companyNameError}</p>
      )}

      {showCompanyDetails && (
        <>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-0">Contacts</Form.Label>
            <ContactPicker
              contacts={managementCompanyContacts}
              contactType="managementCompany"
              directoryId={resolveEntityId(hoa.managementCompanyId)}
              preferredAssociationId={preferredAssociationId}
              showPreferredContactToggle={showPreferredContactToggle}
              isOrderContext={directoryPickerOnly}
              addContactOnly={directoryCreateMode}
              onLinkContact={onLinkContact}
              onUnlinkContact={onUnlinkContact}
              onUpdateContact={onUpdateContact}
              onPreferredContactChange={
                showPreferredContactToggle ? onPreferredContactChange : undefined
              }
            />
          </Form.Group>

          {!contactsOnly && (
            <>
              <Form.Label className="small mb-1">How to order instructions</Form.Label>
              <OrderingPlatformField
                hideLabel
                value={hoa.orderingPlatform || ''}
                onChange={orderingPlatform => updateFields({ orderingPlatform })}
                className="mb-2"
              />
              <div className="mb-2">
                <InstructionFilesUpload
                  compact
                  files={managementCompanyFiles.filter(
                    file => !removedManagementFileIds.includes(String(file?._id || file))
                  )}
                  pendingFiles={pendingManagementFiles}
                  onPendingFilesChange={onPendingFilesChange}
                  onRemovedFileIdsChange={onRemovedFileIdsChange}
                />
              </div>
              <RichTextEditor
                key={`managed-ordering-${index}`}
                className="estoppel-notes-rich-text"
                value={hoa.orderingInstructions || ''}
                onChange={html => updateFields({ orderingInstructions: html })}
                height="145px"
                enableLists
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ManagementCompanyFormFields;
